// Feedback dialog component for collecting user feedback on assistant responses

import React, { useState } from 'react';
import type { FeedbackType } from '../types/chat';

interface FeedbackDialogProps {
  isOpen: boolean;
  feedbackType: FeedbackType;
  onClose: () => void;
  onSubmit: (feedbackText: string) => void;
  initialFeedbackText?: string;
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  isOpen,
  feedbackType,
  onClose,
  onSubmit,
  initialFeedbackText = '',
}) => {
  const [feedbackText, setFeedbackText] = useState(initialFeedbackText);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update feedback text when initialFeedbackText changes (editing existing feedback)
  React.useEffect(() => {
    setFeedbackText(initialFeedbackText || '');
  }, [initialFeedbackText]);

  // Handle Escape key to close dialog
  React.useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(feedbackText);
      setFeedbackText('');
      onClose();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onSubmit('');
    setFeedbackText('');
    onClose();
  };

  return (
    <div className="feedback-dialog-overlay" onClick={onClose}>
      <div className="feedback-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-dialog-header">
          <h3>
            Provide additional feedback {feedbackType === 'POSITIVE' ? '👍 ' : '👎 '}
          </h3>
          <button
            className="feedback-dialog-close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            ×
          </button>
        </div>

        <div className="feedback-dialog-content">
          <p>
            {feedbackType === 'POSITIVE'
              ? 'What did you like about this response?'
              : 'What could be improved?'}
          </p>
          <textarea
            className="feedback-dialog-textarea"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Share your thoughts (optional)..."
            maxLength={1000}
            rows={4}
            autoFocus
          />
          <div className="feedback-dialog-char-count">
            {feedbackText.length}/1000
          </div>
        </div>

        <div className="feedback-dialog-footer">
          <button
            className="feedback-dialog-button feedback-dialog-button-secondary"
            onClick={handleSkip}
            disabled={isSubmitting}
          >
            Skip
          </button>
          <button
            className="feedback-dialog-button feedback-dialog-button-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackDialog;
